/**
 * The condition overlay — the first live client surface rendered from
 * `@annwn/ui` (M6-05).
 *
 * This is the React *island*. The canvas is still the game; `Game`, the input
 * owner, world interpolation and the WebGL/Canvas renderer stay imperative
 * TypeScript. React owns exactly one rail of DOM overlay — the body figure, the
 * part readout and the wound ledger — and it receives already-derived view
 * models and a single callback intent (`onAim`). It never touches a socket or
 * the simulation, and it holds no game state beyond which part the player is
 * currently looking at.
 *
 * RC2-07 took the status tags and the condition sentence off it. They are on
 * the status strip now, where they are visible without the panel being open and
 * where they sit beside the meters they qualify — and a fact drawn twice on one
 * screen is a fact that will eventually be drawn two different ways.
 *
 * `main.ts` stays free of JSX: it calls `mountConditionOverlay` once and then
 * pushes each snapshot in through the returned `update`, exactly as it did with
 * the imperative `BodyDiagram` and `StatusStrip` this replaces. The snapshot
 * cadence, the aim binding, and the "no number reaches the screen" rule are all
 * preserved — the numbers never leave the adapter.
 */

import { useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import type { SelfCombatSnapshot } from '@annwn/protocol';
import { BodyDiagram, WoundLedger } from '@annwn/ui';

import { selectionReadout, toBodyParts, toWoundLedger } from './condition-view-model.js';

export interface ConditionOverlayOptions {
  /** A part was chosen — clicking it is aiming at it, as it always was. */
  onAim(partId: string): void;
}

interface PanelProps extends ConditionOverlayOptions {
  readonly combat: SelfCombatSnapshot | null;
  readonly tick: number;
}

function ConditionPanel({ combat, tick, onAim }: PanelProps) {
  const [selected, setSelected] = useState<string | null>(null);

  if (!combat) {
    return <p className="condition-empty">…</p>;
  }

  const parts = toBodyParts(combat.parts);
  const wounds = toWoundLedger(combat.wounds, combat.parts, tick);
  // Only keep a selection the current body still has — a plan change (aiming at
  // a boar, then at yourself) must not strand the readout on a vanished part.
  const active =
    selected && combat.parts.some((part) => part.id === selected) ? selected : undefined;
  const readout = selectionReadout(combat, tick, active ?? null);

  return (
    <div className="condition-overlay">
      <BodyDiagram
        parts={parts}
        selectedPart={active}
        onSelect={(part) => {
          setSelected(part.id);
          onAim(part.id);
        }}
      />
      {readout ? <p className="condition-readout">{readout}</p> : null}
      <WoundLedger wounds={wounds} label="Wounds" emptyLabel="Unhurt." />
    </div>
  );
}

export interface ConditionOverlayHandle {
  update(combat: SelfCombatSnapshot | null, tick: number): void;
  destroy(): void;
}

/**
 * Mount the React island into `container` and return an imperative handle. Each
 * `update` re-renders with fresh props; React reconciles in place and keeps the
 * panel's own selection state across snapshots, so the figure does not drop the
 * player's focus ten times a second — the same guarantee the imperative diagram
 * gave by updating in place rather than rebuilding.
 */
export function mountConditionOverlay(
  container: HTMLElement,
  options: ConditionOverlayOptions,
): ConditionOverlayHandle {
  const root: Root = createRoot(container);
  let combat: SelfCombatSnapshot | null = null;
  let tick = 0;

  const render = (): void => {
    root.render(<ConditionPanel combat={combat} tick={tick} onAim={options.onAim} />);
  };

  render();

  return {
    update(nextCombat, nextTick) {
      combat = nextCombat;
      tick = nextTick;
      render();
    },
    destroy() {
      root.unmount();
    },
  };
}
